import { $createCodeNode, $isCodeNode, getDefaultCodeLanguage } from '@lexical/code'
import { $generateHtmlFromNodes } from '@lexical/html'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createHeadingNode, $createQuoteNode, $isHeadingNode } from '@lexical/rich-text'
import { $isAtNodeEnd, $isParentElementRTL, $wrapNodes } from '@lexical/selection'
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@nextui-org/react'
import { BsMarkdown } from '@react-icons/all-files/bs/BsMarkdown'
import { CiExport } from '@react-icons/all-files/ci/CiExport'
import { LuFileSpreadsheet } from '@react-icons/all-files/lu/LuFileSpreadsheet'
import { PiFileHtmlBold } from '@react-icons/all-files/pi/PiFileHtmlBold'
import { TbFileTypeDocx } from '@react-icons/all-files/tb/TbFileTypeDocx'
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  BaseSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  EditorState,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND
} from 'lexical'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import XLSX from 'xlsx'
import { CodeIcon } from '~/constants/icon'
import { useTask } from '~/contexts/TaskContext'
import { CommandType, commandTypeFromJSON } from '~/protos/agents'
import { extractDWLCode, saveAsFile } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'
import { nodes2docx } from '../nodes2docx'

const LowPriority = 1
export const blockTypeToBlockName = {
  code: 'Code Block',
  h1: 'Large Heading',
  h2: 'Small Heading',
  h3: 'Heading',
  h4: 'Heading',
  h5: 'Heading',
  ol: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
  ul: 'Bulleted List'
} as const

export function Divider(): JSX.Element {
  return <div className='divider' />
}

export function positionEditorElement(editor: HTMLElement, rect: DOMRect | null): void {
  if (rect === null) {
    editor.style.opacity = '0'
    editor.style.top = '-1000px'
    editor.style.left = '-1000px'
  } else {
    editor.style.opacity = '1'
    editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`
    editor.style.left = `${
      rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
    }px`
  }
}

export function FloatingLinkEditor({ editor }: { editor: LexicalEditor }): JSX.Element {
  const editorRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const mouseDownRef = useRef(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [isEditMode, setEditMode] = useState(false)
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null)

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection)
      const parent = node.getParent()
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL())
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL())
      } else {
        setLinkUrl('')
      }
    }
    const editorElem = editorRef.current
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    if (editorElem === null) {
      return
    }

    const rootElement = editor.getRootElement()
    if (
      selection !== null &&
      !nativeSelection?.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection!.anchorNode)
    ) {
      const domRange = nativeSelection!.getRangeAt(0)
      let rect
      if (nativeSelection!.anchorNode === rootElement) {
        let inner = rootElement
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild as HTMLDivElement
        }
        rect = inner.getBoundingClientRect()
      } else {
        rect = domRange.getBoundingClientRect()
      }

      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect)
      }
      setLastSelection(selection)
    } else if (!activeElement || activeElement.className !== 'link-input') {
      positionEditorElement(editorElem, null)
      setLastSelection(null)
      setEditMode(false)
      setLinkUrl('')
    }

    return true
  }, [editor])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }: { editorState: EditorState }) => {
        editorState.read(() => {
          updateLinkEditor()
        })
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor()
          return true
        },
        LowPriority
      )
    )
  }, [editor, updateLinkEditor])

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor()
    })
  }, [editor, updateLinkEditor])

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditMode])

  return (
    <div ref={editorRef} className='link-editor'>
      {isEditMode ? (
        <input
          ref={inputRef}
          className='link-input'
          value={linkUrl}
          onChange={event => {
            setLinkUrl(event.target.value)
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault()
              if (lastSelection !== null) {
                if (linkUrl !== '') {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl)
                }
                setEditMode(false)
              }
            } else if (event.key === 'Escape') {
              event.preventDefault()
              setEditMode(false)
            }
          }}
        />
      ) : (
        <>
          <div className='link-input'>
            <a href={linkUrl} target='_blank' rel='noopener noreferrer'>
              {linkUrl}
            </a>
            <div
              className='link-edit'
              role='button'
              tabIndex={0}
              onMouseDown={event => event.preventDefault()}
              onClick={() => {
                setEditMode(true)
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}

export function Select({
  onChange,
  className,
  options,
  value
}: {
  onChange: any
  className: string
  options: string[]
  value: string
}): JSX.Element {
  return (
    <select className={className} onChange={onChange} value={value}>
      <option hidden={true} value='' />
      {options.map(option => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  )
}

export function getSelectedNode(selection: any) {
  const anchor = selection.anchor
  const focus = selection.focus
  const anchorNode = selection.anchor.getNode()
  const focusNode = selection.focus.getNode()
  if (anchorNode === focusNode) {
    return anchorNode
  }
  const isBackward = selection.isBackward()
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode
  }
}

export function BlockOptionsDropdownList({
  editor,
  blockType,
  toolbarRef,
  setShowBlockOptionsDropDown
}: {
  editor: any
  blockType: string
  toolbarRef: any
  setShowBlockOptionsDropDown: any
}): JSX.Element {
  const dropDownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const toolbar = toolbarRef.current
    const dropDown = dropDownRef.current

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect()
      dropDown.style.top = `${top + 40}px`
      dropDown.style.left = `${left}px`
    }
  }, [dropDownRef, toolbarRef])

  useEffect(() => {
    const dropDown = dropDownRef.current
    const toolbar = toolbarRef.current

    if (dropDown !== null && toolbar !== null) {
      const handle = (event: MouseEvent) => {
        const target = event.target as Node

        if (!dropDown.contains(target) && !toolbar.contains(target)) {
          setShowBlockOptionsDropDown(false)
        }
      }
      document.addEventListener('click', handle)

      return () => {
        document.removeEventListener('click', handle)
      }
    }
  }, [dropDownRef, setShowBlockOptionsDropDown, toolbarRef])

  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode())
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatLargeHeading = () => {
    if (blockType !== 'h1') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode('h1'))
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatSmallHeading = () => {
    if (blockType !== 'h2') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode('h2'))
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND)
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND)
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode())
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createCodeNode())
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  return (
    <div className='dropdown' ref={dropDownRef}>
      <button className='item' onClick={formatParagraph}>
        <span className='icon paragraph' />
        <span className='text'>Normal</span>
        {blockType === 'paragraph' && <span className='active' />}
      </button>
      <button className='item' onClick={formatLargeHeading}>
        <span className='icon large-heading' />
        <span className='text'>Large Heading</span>
        {blockType === 'h1' && <span className='active' />}
      </button>
      <button className='item' onClick={formatSmallHeading}>
        <span className='icon small-heading' />
        <span className='text'>Small Heading</span>
        {blockType === 'h2' && <span className='active' />}
      </button>
      <button className='item' onClick={formatBulletList}>
        <span className='icon bullet-list' />
        <span className='text'>Bullet List</span>
        {blockType === 'ul' && <span className='active' />}
      </button>
      <button className='item' onClick={formatNumberedList}>
        <span className='icon numbered-list' />
        <span className='text'>Numbered List</span>
        {blockType === 'ol' && <span className='active' />}
      </button>
      <button className='item' onClick={formatQuote}>
        <span className='icon quote' />
        <span className='text'>Quote</span>
        {blockType === 'quote' && <span className='active' />}
      </button>
      <button className='item' onClick={formatCode}>
        <span className='icon code' />
        <span className='text'>Code Block</span>
        {blockType === 'code' && <span className='active' />}
      </button>
    </div>
  )
}

interface ToolbarPluginProps {
  rawMarkdown?: string
}

export default function ToolbarPlugin({ rawMarkdown }: ToolbarPluginProps): JSX.Element {
  const [editor] = useLexicalComposerContext()
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>('paragraph')
  const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] = useState(false)
  const [isRTL, setIsRTL] = useState(false)
  const [isLink, setIsLink] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isCode, setIsCode] = useState(false)
  const { task } = useTask()
  const commandType = commandTypeFromJSON(task?.cmd_type)

  const isDataWeaveTask = [
    CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
    CommandType.GENERATE_DATAWEAVE,
    CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE
  ].includes(commandType || CommandType.NONE)
  const isMappingTableTask = commandType === CommandType.GENERATE_MAPPING_TABLE

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      const element =
        anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()
      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode)
          const type = parentList ? parentList.getTag() : element.getTag()
          setBlockType(type)
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType()
          setBlockType(type as keyof typeof blockTypeToBlockName)
        }
      }
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
      setIsCode(selection.hasFormat('code'))
      setIsRTL($isParentElementRTL(selection))

      const node = getSelectedNode(selection)
      const parent = node.getParent()
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }
    }
  }, [editor])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar()
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar()
          return false
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        payload => {
          setCanUndo(payload)
          return false
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        payload => {
          setCanRedo(payload)
          return false
        },
        LowPriority
      )
    )
  }, [editor, updateToolbar])

  const onAction = (key: string | number) => {
    const editorState = editor.getEditorState()

    switch (key) {
      case 'html': {
        editorState.read(async () => {
          const html = $generateHtmlFromNodes(editor)
          saveAsFile({ html }, 'text/html')
        })
        break
      }
      case 'markdown': {
        editor.update(() => {
          const html = $generateHtmlFromNodes(editor)
          const markdown = rawMarkdown
          editorState.read(async () => {
            saveAsFile({ html, markdown }, 'text/markdown')
          })
        })
        break
      }
      case 'docx': {
        editorState.read(() => {
          const html = $generateHtmlFromNodes(editor)
          const doc = nodes2docx()
          saveAsFile(
            { html, doc },
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          )
        })
        break
      }
      case 'xlsx': {
        if (toolbarRef.current) {
          const lexicalElement = toolbarRef.current.parentElement
          const table = lexicalElement?.querySelector('table')
          if (table) {
            const wb = XLSX.utils.table_to_book(table)
            XLSX.writeFile(wb, 'MappingTable.xlsx')
          } else {
            BetterToast.error('No table found to export')
          }
        }
        break
      }
      case 'dwl': {
        editorState.read(() => {
          let plainText = editor.getEditorState().read(() => {
            return $getRoot().getTextContent()
          })
          plainText = extractDWLCode(plainText)
          const html = $generateHtmlFromNodes(editor)
          saveAsFile({ dwl: plainText, html }, 'text/dwl')
        })
        break
      }
    }
  }

  return (
    <div className='toolbar text-black !m-0 !py-0 !px-4 !mb-4' ref={toolbarRef}>
      <div className='flex items-center justify-end w-full'>
        <div className='flex justify-center items-center'>
          <div className='ml-4'>
            <Dropdown
              classNames={{
                content: 'rounded-md'
              }}
            >
              <DropdownTrigger>
                <button className='toolbar-item spaced' aria-label='Export'>
                  <div className='flex items-center'>
                    <CiExport size={32} className='mr-[5px]' />
                    Export
                  </div>
                </button>
              </DropdownTrigger>
              {isDataWeaveTask ? (
                <DropdownMenu aria-label='Static Actions' onAction={onAction}>
                  <DropdownItem key='dwl'>
                    <div className='flex items-center text-default-800 text-xs'>
                      <CodeIcon className='mr-[8px]' />
                      Export as DataWeave
                    </div>
                  </DropdownItem>
                </DropdownMenu>
              ) : (
                <DropdownMenu aria-label='Static Actions' onAction={onAction}>
                  <DropdownItem key='html'>
                    <div className='flex items-center text-default-800 text-xs'>
                      <PiFileHtmlBold size={32} className='mr-[8px]' />
                      Export as HTML
                    </div>
                  </DropdownItem>
                  {isMappingTableTask ? (
                    <DropdownItem key='xlsx'>
                      <div className='flex items-center text-default-800 text-xs'>
                        <LuFileSpreadsheet size={32} className='mr-[8px]' />
                        Export as XLSX
                      </div>
                    </DropdownItem>
                  ) : (
                    <DropdownItem key='docx'>
                      <div className='flex items-center text-default-800 text-xs'>
                        <TbFileTypeDocx size={32} className='mr-[8px]' />
                        Export as DOCX
                      </div>
                    </DropdownItem>
                  )}
                  <DropdownItem key='markdown'>
                    <div className='flex items-center text-default-800 text-xs'>
                      <BsMarkdown size={32} className='mr-[8px]' />
                      Export as Markdown
                    </div>
                  </DropdownItem>
                </DropdownMenu>
              )}
            </Dropdown>
          </div>
        </div>
      </div>

      {isLink && createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
      {/* {supportedBlockTypes.has(blockType) && (
        <>
          <button
            className='toolbar-item block-controls'
            onClick={() => setShowBlockOptionsDropDown(!showBlockOptionsDropDown)}
            aria-label='Formatting Options'
          >
            <span className={'icon block-type ' + blockType} />
            <span className='text'>{blockTypeToBlockName[blockType]}</span>
            <i className='chevron-down' />
          </button>
          {showBlockOptionsDropDown &&
            createPortal(
              <BlockOptionsDropdownList
                editor={editor}
                blockType={blockType}
                toolbarRef={toolbarRef}
                setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
              />,
              document.body
            )}
          <Divider />
        </>
      )} */}
      {/* {blockType === 'code' ? (
        <>
          <Select
            className='toolbar-item code-language'
            onChange={onCodeLanguageSelect}
            options={codeLanguges}
            value={codeLanguage}
          />
          <i className='chevron-down inside' />
        </>
      ) : ( */}

      {/* )} */}
    </div>
  )
}
